import React, {useState, useEffect} from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { deregisterSpaUser, registerSpaUser } from "../../../redux/actions/spa.actions";
import UserSelect from "../../../components/common/UserSelect/UserSelect";
import RootState from "../../../redux/states/root.state";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";

interface ModalProps {
    isOpen: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    user: any;
    spa: any;
}

const RegisterUserModal: React.FC<ModalProps> = ({isOpen, setOpen, user, spa}) => {
  const dispatch: AppDispatch = useDispatch();
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isDeregistering, setIsDeregistering] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const {loading} = useSelector((state: RootState)=>state.spa);

  useEffect(() => {
    if (isOpen){
      setError(false);
      setSuccess(false);
      setIsDeregistering(false);
      setSelectedUser(user ?? null);
    }
  }, [isOpen, user]);

  const handleClose = () => {
    setOpen(false);
  };

  // Disable confirm until a different user (or removal) is chosen.
  const isRemoving = selectedUser === 'none';
  const noChange =
    (!selectedUser) ||
    (isRemoving && !user) ||
    (selectedUser?.id && selectedUser.id === user?.id);

  const handleConfirm = async () => {
    if (!spa || noChange) return;

    setIsDeregistering(isRemoving);

    const result = isRemoving
      ? await dispatch(deregisterSpaUser({guid: spa.id, user_id: user?.id}))
      : await dispatch(registerSpaUser({guid: spa.id, user_id: selectedUser.id}));

    if (result.type.endsWith('/fulfilled')) {
      setSuccess(true);
    } else {
      setError(true);
    }
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      busy={loading}
      tone={loading ? undefined : error ? "error" : success ? "success" : undefined}
      title={
        loading ? "Updating…"
        : error ? "Couldn't update the owner"
        : success ? (isDeregistering ? "Owner removed" : "Owner updated")
        : user ? "Change spa owner" : "Register spa owner"
      }
      subtitle={!loading && !error && !success ? "Search by email, then pick the user this spa belongs to." : undefined}
      actions={loading ? undefined : error || success ?
        <button className="btn btn-primary" onClick={handleClose}>Done</button>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className={`btn ${isRemoving ? 'btn-danger' : 'btn-primary'}`} disabled={noChange} onClick={handleConfirm}>
            {isRemoving ? 'Remove owner' : 'Confirm'}
          </button>
        </>
      }
    >
      {loading ?
        <ModalLoading/>
        : error ?
        <p>An error occurred while updating the spa user, please try again later.</p>
        : success ?
        <p>{isDeregistering ? 'Spa successfully deregistered from user.' : 'Spa successfully registered to user.'}</p>
        :
        <>
          <div className="dlg-current">
            <span className="dlg-label">Current owner</span>
            <strong>{user?.name ?? user?.username ?? user?.email ?? 'None'}</strong>
          </div>
          <div className="dlg-section">
            <UserSelect setSelectedUser={setSelectedUser} currentUser={selectedUser ?? user}/>
          </div>
        </>
      }
    </ModalShell>
  );
};

export default RegisterUserModal;
